import { IOnInit, IPromise, IScope } from "angular";
import { MachineConfigurationView } from "../../../../models/machineConfiguration";
import { IFieldArray } from "AngularFormly";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import { RecordFileTemplateOptions } from "../../../../../../records/forms/formly/types";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";
import { cloneDeep } from "lodash";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import { warehouseTypeName } from "../../../../configs/lookup";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as machineLookupManagementServiceName } from "../../../../../shared/lookups";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../../records/utils";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../../../../../records/changeJournal/changeNoteModalService";

interface MachinesWarehouseFormModel {
  MachineChanges?: MachineWarehouseMachine[];
  Date?: Date;
  Files?: File[];
}

interface MachineWarehouseMachine {
  MachineId: number;
  [key: string]: any;
}
export class MachinesMoveViewComponentController implements IOnInit {
  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    machineLookupManagementServiceName,
    "$scope",
    RecordsUtilsServiceName,
    ChangeJournalModalServiceName,
  ];

  formFields!: IFieldArray;
  machineInfo!: {
    machineConfigurations: MachineConfigurationView[];
  };

  /* start bindings */
  readonly machineConfigurations!: MachineConfigurationView[];
  /* end binding */

  model!: MachinesWarehouseFormModel;

  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly machineLookupManagementService: LookupManagementService,
    $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
    private readonly changeJournalModalService: ChangeJournalModalService
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private initializeFormFields(machineInfo: {
    machineConfigurations: MachineConfigurationView[];
  }) {
    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Change Date",
            required: true,
          }),
          this.fieldConfigurationHelperService.lookupField(
            this.machineLookupManagementService,
            {
              key: "MachineWarehouseTypeId",
              label: "Warehouse Type",
              objectName: warehouseTypeName,
            }
          ),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Machines",
        key: "MachinesHeader",
      }),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        repeaterFields: fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleReadonlyField({
              key: "MachineId",
              className: "col-xs-3",
              label: "Serial Number",
              formatters: [
                (machineId) =>
                  this.getMachineConfigurationView(<number>machineId)
                    ?.SerialNumber,
              ],
            }),
            fieldHelpers.simpleStringField({
              key: "WarehouseLocation",
              name: "WarehouseLocation",
              label: "Location",
            }),
            /* TODO - add client specific warehouse fields here */
            {
              key: "copyDown",
              name: "copyDown",
              className: "col-xs-3",
              templateOptions: {
                label: " ",
              },
              template: `
<div>
  <div class="pull-right">
    <div class="btn-group">
      <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
        <i class="glyphicon glyphicon-arrow-down"></i>
      </div>
    </div>
  </div>
</div>
        `,
              wrapper: ["bootstrapLabel", "bootstrapHasError"],
            },
          ],
          4
        ),
        displayNameSingular: "Machine",
        displayNamePlural: "Machines",
        showHeader: false,
      }),
      // {
      //   key: "machineConfigurations",
      //   model: machineInfo,
      //   template: require("!html-loader!../sharedTemplates/machineList.html")
      //     .default,
      // },
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Compliance Files",
          group: "Compliance",
        },
      },
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Software Compliance Notices",
          group: "SoftwareCompliance",
        },
      },
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: {
          allowMultiple: true,
          label: "Internal Documentation",
          group: "InternalDocumentation",
        },
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Date: undefined,
      Files: [],
      MachineChanges: (this.machineConfigurations ?? []).map((mc) => {
        return {
          MachineId: mc.MachineId,
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields(this.machineInfo);
  }

  submit(formViewController: FormViewController): IPromise<void> {
    return this.changeJournalModalService
      .getAndSetChangeJournalNote(this.model)
      .then(() =>
        this.formViewService.submitForm({
          url: `/api/machines/warehouse`,
          formObject: this.model,
          formViewController,
          method: "post",
        })
      )
      .then(() => {
        this.$state.go("^.grid", undefined, { reload: true });
      });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid");
  }

  private copyDown(modelClicked: any) {
    const machineChanges = this.model.MachineChanges ?? [];
    const copyIndex = machineChanges.indexOf(modelClicked);

    const copyDownModel = {
      WarehouseLocation: modelClicked.WarehouseLocation,
      /* TODO - add specific fields that will be copied down when copy down button is clicked */
    };

    for (let i = copyIndex + 1; i < machineChanges.length; i++) {
      const newModel = {
        ...copyDownModel,
        MachineId: machineChanges[i].MachineId,
      };
      this.recordUtils.assignId(newModel);
      machineChanges.splice(i, 1, newModel);
    }
  }

  private getMachineConfigurationView(machineId: number) {
    return this.machineConfigurations.find((mc) => mc.MachineId === machineId);
  }
}
